import React, { useEffect, useState } from "react";
import { X, Trash2, Plus } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { API_BASE } from "../DataContext";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });

export function GRNModal({ token, po, onClose, onSaved }) {
  const [grns, setGrns] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState("");
  const [lines, setLines] = useState({}); // {item_index: qty}
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => {
    fetch(`${API_BASE}/api/admin/grns?po_id=${po.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then(setGrns);
  }, [po.id, token]);

  const received = {};
  grns.forEach((g) => (g.items || []).forEach((ln) => { received[ln.item_index] = (received[ln.item_index] || 0) + Number(ln.received_qty || 0); }));

  const submit = async () => {
    setError("");
    const items = Object.entries(lines).filter(([, q]) => Number(q) > 0).map(([i, q]) => ({ item_index: Number(i), received_qty: Number(q) }));
    if (items.length === 0) return setError("Enter received quantity for at least one item");
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/api/admin/grns`, { method: "POST", headers, body: JSON.stringify({ po_id: po.id, grn_date: date, remarks, items }) });
      if (!r.ok) throw new Error((await r.json()).detail || "Save failed");
      onSaved && onSaved();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const removeGrn = async (g) => {
    if (!confirm(`Delete GRN ${g.grn_number}? This will restore the balance quantity.`)) return;
    const r = await fetch(`${API_BASE}/api/admin/grns/${g.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) { setGrns(grns.filter((x) => x.id !== g.id)); onSaved && onSaved(); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="grn-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
          <div>
            <h2 className="text-xl font-bold text-[#0a2a5e]">GRN — {po.po_number}</h2>
            <p className="text-xs text-slate-500 mt-0.5">Record Goods Receipt Note against this Purchase Order</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-6">
          {/* Existing GRNs */}
          {grns.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Existing GRNs</div>
              <div className="border border-slate-200 rounded-lg overflow-hidden text-sm">
                <table className="w-full">
                  <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                    <tr><th className="px-3 py-2 text-left">GRN No</th><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Items</th><th></th></tr>
                  </thead>
                  <tbody>
                    {grns.map((g) => (
                      <tr key={g.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-mono text-xs">{g.grn_number}</td>
                        <td className="px-3 py-2">{g.grn_date}</td>
                        <td className="px-3 py-2 text-slate-600">
                          {(g.items || []).map((ln, i) => (<div key={i}>{po.items[ln.item_index]?.item || `#${ln.item_index}`}: {ln.received_qty}</div>))}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => removeGrn(g)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* New GRN */}
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Record New Receipt</div>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">GRN Date</label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="grn-date" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Remarks</label>
                <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="LR no / vehicle no / notes" />
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Item</th>
                    <th className="px-3 py-2 text-right">Ordered</th>
                    <th className="px-3 py-2 text-right">Already Received</th>
                    <th className="px-3 py-2 text-right">Balance</th>
                    <th className="px-3 py-2 text-right w-32">Receive Now</th>
                  </tr>
                </thead>
                <tbody>
                  {po.items.map((it, i) => {
                    const already = received[i] || 0;
                    const balance = Number(it.qty) - already;
                    return (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-2">
                          <div className="font-semibold">{it.item}</div>
                          {it.description && <div className="text-xs text-slate-500">{it.description}</div>}
                        </td>
                        <td className="px-3 py-2 text-right">{it.qty} {it.unit}</td>
                        <td className="px-3 py-2 text-right text-slate-500">{already}</td>
                        <td className="px-3 py-2 text-right font-semibold text-emerald-700">{balance}</td>
                        <td className="px-3 py-2">
                          <input type="number" step="any" max={balance} disabled={balance <= 0} value={lines[i] || ""}
                            onChange={(e) => setLines({ ...lines, [i]: e.target.value })}
                            data-testid={`grn-recv-${i}`}
                            className="w-full px-2 py-1 rounded border border-slate-200 text-right text-sm disabled:bg-slate-100" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {error && <div className="text-sm text-red-600 mt-3">{error}</div>}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="grn-save-btn" className="bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white rounded-full">
              {saving ? "Saving…" : "Record GRN"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
